import { useMemo } from "react";
import { Transaction_Type } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import { css } from "../../../styled-system/css";
import Header from "../../components/header";
import type { ConnectClient } from "../../libs/api";
import { formatDate } from "../../libs/date";
import { AppError, ERROR_CODES } from "../../libs/errors";
import { t } from "../../libs/i18n";
import {
  MonthlyTransactionBlock,
  type Tx,
} from "./components/monthly-transaction-block";
import { MonthlyTransactionsSkeleton } from "./components/monthly-transactions-skeleton";
import { useInfiniteTransactions } from "./hooks/use-infinite-transactions";
import {
  baseTransaction,
  getTransactionIconUrl,
  getTransactionName,
} from "./utils/transaction-helpers";

type MonthlyTransactions = {
  [month: string]: Tx[];
};

type TransactionListProps = {
  client: ConnectClient;
};

export function TransactionList({ client }: TransactionListProps) {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteTransactions(client);

  const transactionGroups = useMemo(() => {
    return data?.pages.flatMap((p) => p.transactionGroups) ?? [];
  }, [data]);

  const monthlyTransactions: MonthlyTransactions = useMemo(() => {
    const map: MonthlyTransactions = {};
    const sortedGroups = [...transactionGroups].sort((a, b) => {
      const baseA = baseTransaction(a.transactionDetails);
      const baseB = baseTransaction(b.transactionDetails);
      return (
        Number(baseB.transaction?.createdAt?.seconds) -
        Number(baseA.transaction?.createdAt?.seconds)
      );
    });

    for (const txg of sortedGroups) {
      const month = formatDate(
        new Date(
          Number(txg.transactionDetails[0].transaction?.createdAt?.seconds) *
            1000,
        ),
        "month",
      );
      if (!map[month]) {
        map[month] = [];
      }

      const amount = txg.transactionDetails
        .filter((d) => {
          if (
            d.transaction?.type === Transaction_Type.GRANT ||
            d.transaction?.type === Transaction_Type.PAYMENT ||
            d.transaction?.type === Transaction_Type.TRANSFER_IN ||
            d.transaction?.type === Transaction_Type.TRANSFER_OUT ||
            d.transaction?.type === Transaction_Type.PAYMENT_PSINTERNAL ||
            d.transaction?.type === Transaction_Type.CHARGE
          ) {
            return true;
          }
          return false;
        })
        .reduce((acc, ts) => acc + Number(ts.transaction?.amount || 0), 0);

      let type = txg.transactionDetails[0].transaction?.type;

      const refund = txg.transactionDetails.find(
        (d) => d.transaction?.type === Transaction_Type.REFUND,
      );
      if (refund) {
        type = Transaction_Type.REFUND;
      }

      if (!type) {
        throw new AppError(ERROR_CODES.TRANSACTION_TYPE_UNSPECIFIED);
      }

      const storeIconUrl = txg.transactionDetails[0].store?.iconUrl;
      const iconUrl = getTransactionIconUrl(storeIconUrl, type);

      const base = baseTransaction(txg.transactionDetails);

      map[month].push({
        id: txg.id,
        amount,
        type,
        name: getTransactionName(txg.transactionDetails[0]),
        iconUrl,
        createdAt: new Date(
          Number(base.transaction?.createdAt?.seconds) * 1000,
        ),
      });
    }
    return map;
  }, [transactionGroups]);

  return (
    <>
      <Header title={t("取引履歴")} />
      <div
        className={css({
          py: "16px",
          pb: "64px",
        })}
      >
        <div
          className={css({
            display: "flex",
            gap: "24px",
            flexDirection: "column",
          })}
        >
          {isLoading ? (
            <>
              <MonthlyTransactionsSkeleton />
              <MonthlyTransactionsSkeleton />
            </>
          ) : (
            <>
              {Object.keys(monthlyTransactions).length === 0 &&
                !hasNextPage && (
                  <div
                    className={css({
                      color: "text.secondary",
                      textAlign: "center",
                      mt: "16px",
                    })}
                  >
                    {t("取引履歴がありません")}
                  </div>
                )}

              {Object.keys(monthlyTransactions).length === 0 && hasNextPage && (
                <div
                  className={css({
                    color: "text.secondary",
                    textAlign: "center",
                    mt: "16px",
                  })}
                >
                  {t("直近の取引履歴はありません")}
                </div>
              )}

              {Object.entries(monthlyTransactions).map(([month, txs]) => (
                <MonthlyTransactionBlock key={month} month={month} txs={txs} />
              ))}

              {!isLoading && (
                <div
                  className={css({
                    display: "flex",
                    justifyContent: "center",
                    mt: "8px",
                    mb: "16px",
                  })}
                >
                  <button
                    type="button"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className={css({
                      px: "16px",
                      color: "text.primary",
                      borderRadius: "4px",
                      fontWeight: "600",
                      fontSize: "14px",
                      _disabled: {
                        opacity: 0.5,
                        cursor: "not-allowed",
                      },
                    })}
                  >
                    {isFetchingNextPage ? t("読み込み中...") : t("もっと見る")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
